import cx from "classnames";
import { useRouter } from "next/compat/router";
import Link from "next/link";
import { useParams } from "next/navigation";
import { HTMLProps, useMemo, useState } from "react";
import Image from "@/components/Image";
import { NAV_LINK_CLICKED } from "@/constants/amplitude";
import { NestedLink } from "@/graphql/gql/graphql";
import { useAmplitude } from "@/utils/hooks/useAmplitude";
import SportSubMenu from "../SportSubMenu";
import Styles from "./MenuItem.module.scss";

interface MenuItemProps {
  menuLink: NestedLink;
  sport?: string;
  sportSlug: string | undefined;
}

const Container = ({ children, ...attributes }: HTMLProps<HTMLSpanElement>) => (
  <span {...attributes} className={Styles.noLinkContainer}>
    {children}
  </span>
);
const MenuItem = ({ menuLink, sport, sportSlug }: MenuItemProps) => {
  const { text, imageUrl, iconUrl, url } = menuLink;
  const [isHover, setHover] = useState(false);
  const params = useParams<{ slug: string }>();
  const router = useRouter();
  const isSelectedSport = useMemo(
    () =>
      url.toLowerCase() ===
        `/${((router?.query.slug ?? params?.slug)?.toString() ?? sportSlug)?.toLowerCase()}` ||
      text.toLocaleLowerCase() === (sport ?? "").toLocaleLowerCase(),
    [url, text, router?.query.slug, sport, sportSlug, params?.slug]
  );
  const { logAmplitudeEvent } = useAmplitude();

  // menu item hover logic for desktop: dropdown opens only when hovering the text/icon (not empty container space)
  const handleMouseEnterText = () => {
    setHover(true);
  };

  const handleMouseEnterContainer = (e: React.MouseEvent<HTMLDivElement>) => {
    const relatedTarget = e.relatedTarget;
    if (!(relatedTarget instanceof HTMLElement)) return;

    const fromMenuItemElement = relatedTarget.closest("[data-menu-item]");
    const fromOpenDropdown = fromMenuItemElement?.classList.contains(
      Styles.hoverItem
    );

    // keep dropdown open when moving horizontally between menu items (only if source had dropdown open)
    if (fromOpenDropdown) {
      setHover(true);
    }
  };

  const handleMouseLeaveContainer = (e: React.MouseEvent<HTMLDivElement>) => {
    const toOwnChild =
      e.relatedTarget instanceof HTMLElement &&
      e.currentTarget.contains(e.relatedTarget);

    // close dropdown unless moving to a different submenu
    if (!toOwnChild) {
      setHover(false);
    }
  };

  const MenuDisplay = url ? Link : Container;
  return (
    <div
      data-menu-item
      className={cx(Styles.hoverContain, isHover && Styles.hoverItem)}
      onMouseEnter={handleMouseEnterContainer}
      onMouseLeave={handleMouseLeaveContainer}
    >
      <MenuDisplay
        prefetch={url ? false : undefined}
        href={url || "/"}
        onClick={() =>
          logAmplitudeEvent(NAV_LINK_CLICKED, {
            "Link Text": imageUrl ? "fd_logo" : text,
            "Link URL": url,
            Module: "navigation_menu",
            Menu: "main_nav",
          })
        }
      >
        <>
          {url ? (
            <span
              className={cx(
                imageUrl ? Styles.imageContainer : Styles.container,
                isSelectedSport && Styles.selected
              )}
            >
              <button
                type="button"
                data-menu-text
                onMouseEnter={handleMouseEnterText}
                aria-label={imageUrl ? "menu item" : text}
              >
                {imageUrl ? (
                  <Image
                    style={{ display: "inline" }}
                    width={105}
                    height={30}
                    alt="menu item"
                    src={imageUrl}
                  />
                ) : (
                  <>
                    <Image
                      alt={`${text} icon`}
                      src={iconUrl ?? ""}
                      height={16}
                      width={16}
                    />
                    {text}
                  </>
                )}
              </button>
            </span>
          ) : (
            <span className={Styles.container}>
              <button
                type="button"
                data-menu-text
                onMouseEnter={handleMouseEnterText}
                aria-label={text}
              >
                {text}
              </button>
            </span>
          )}
        </>
      </MenuDisplay>
      {!imageUrl ? (
        <div className={Styles.submenu}>
          <SportSubMenu
            hide={() => setHover(false)}
            submenu={menuLink}
            more={!menuLink.url}
          />
        </div>
      ) : (
        ""
      )}
    </div>
  );
};

export default MenuItem;
